import { getEvaluationRules } from '../evaluation-rules-load';
import type { FormValues, Analysis, AnalysisResultItem, SopIntakeGoalFrameState } from '@/lib/types';
import type { TestData } from '@/components/sop-intake/types';
import type { SopPhaseGoals } from '../rule-params';
import type {
  SopPhaseCode,
  SopPatternCode,
  LateralizationCode,
  CollapsedTokens,
  SopPostureResult,
  OverallDisplay,
  SectionSummaries,
} from './types';
import { classifyIntakeGoalFrameState } from '@/lib/posture-analysis/sop-intake-goal-band';

// ---------------------------------------------------------------------------
// Config Loading (single YAML: src/lib/posture-analysis/evaluation-rules.yaml)
// ---------------------------------------------------------------------------

const evaluationRules = () => getEvaluationRules() as any;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function parseNum(v: any): number | null {
  if (v === null || v === undefined || v === '' || v === 'Select...') return null;
  const m = String(v).match(/-?\d+(?:\.\d+)?/);
  return m ? parseFloat(m[0]) : null;
}

function parseValue(key: string, formValues: FormValues, testData: TestData): number | null {
  const val = formValues[key];
  if (val === null || val === undefined || val === '' || val === 'Select...') return null;

  const testKey = key.replace(/^(L_|R_)/, '');
  const testItem = testData[testKey];
  
  // Ignore text inputs for numerical parsing, except for explicit logic overrides if any
  if (testItem && testItem.type !== 'text') {
    return parseNum(val);
  }
  return null;
}

/**
 * Intake framing vs nominal goal (YAML-driven):
 * - limitation (red): nominal - measured > intakeLimitationMinShortfallDeg (strict)
 * - hypermobile (yellow): measured - nominal > intakeHypermobileMinSurplusDeg (strict)
 * - pass (green): otherwise
 * Empty / non-numeric measured → none (neutral border).
 */
export function getSopIntakeGoalFrameState(measured: number | null, nominal: number): SopIntakeGoalFrameState {
  if (measured === null || !Number.isFinite(measured) || !Number.isFinite(nominal)) return 'none';
  const bandStatus = classifyIntakeGoalFrameState(measured, nominal);
  if (bandStatus === 'below-goal') return 'limitation';
  if (bandStatus === 'hypermobile') return 'hypermobile';
  return 'pass';
}

// ---------------------------------------------------------------------------
// Core Exports
// ---------------------------------------------------------------------------

/**
 * Computes Movement Limitations directly from SOP passive tests.
 */
export function computeMovementLimitations(formValues: FormValues): string[] {
  const limitations: string[] = [];
  const limits = evaluationRules().sop.limitations;

  const L = (k: string) => parseNum(formValues[`L_${k}`]);
  const R = (k: string) => parseNum(formValues[`R_${k}`]);
  const avg = (l: number | null, r: number | null) => (l !== null && r !== null) ? (l + r) / 2 : null;

  const avgIR = avg(L('ShoulderIR'), R('ShoulderIR'));
  const avgER = avg(L('ShoulderER'), R('ShoulderER'));
  const avgFIR = avg(L('FemoralIR'), R('FemoralIR'));
  const avgFER = avg(L('FemoralER'), R('FemoralER'));
  const avgPR = avg(L('PelvicRotation'), R('PelvicRotation'));
  const avgHA = avg(L('HipAbduction'), R('HipAbduction'));

  if (avgIR !== null && avgIR < limits.avgShoulderIRMin) {
    limitations.push("Very Limited Anterior Rib Cage Expansion (IR)");
  }
  if (avgER !== null && avgER < limits.avgShoulderERMin) {
    limitations.push("Very Limited Posterior Rib Cage Expansion (ER)");
  }
  if (avgFIR !== null && avgFIR < limits.avgFemoralIRMin) {
    limitations.push("Very Limited Lower Body IR");
  }
  if (avgFER !== null && avgFER < limits.avgFemoralERMin) {
    limitations.push("Very Limited Lower Body ER");
  }
  if ((avgPR !== null && avgPR < limits.avgPelvicRotationMin) || (avgHA !== null && avgHA < limits.avgHipAbductionMin)) {
    if (!limitations.includes("Very Limited Lower Body IR")) {
      limitations.push("Very Limited Lower Body IR");
    }
  }

  return limitations;
}

/**
 * Derives the Phase from SOP tests against phase goals.
 * Replaces both `computePhaseCode` and `computePhaseFromSop`.
 */
export function computeSopPhase(formValues: FormValues, dynamicGoals?: Partial<SopPhaseGoals>): SopPhaseCode {
  const cur = evaluationRules().ruleVersions?.current ?? {};
  const baseP2 = (cur.P2 as { sopPhaseGoals?: SopPhaseGoals })?.sopPhaseGoals ?? {};
  const baseP3 = (cur.P3 as { sopPhaseGoals?: SopPhaseGoals })?.sopPhaseGoals ?? {};
  const p2 = { ...baseP2, ...dynamicGoals };
  const p3 = { ...baseP3, ...dynamicGoals };

  const L = (k: string) => parseNum(formValues[`L_${k}`]);
  const R = (k: string) => parseNum(formValues[`R_${k}`]);
  
  const meets = (key: string, min: number | undefined | null): boolean => {
    if (min === null || min === undefined) return false;
    const lVal = L(key);
    const rVal = R(key);
    if (lVal === null || rVal === null) return false;
    return lVal >= min && rVal >= min;
  };

  const meetsP3 = (
    meets('ShoulderAbduction', p3.shoulderAbdMinDeg) &&
    meets('PelvicRotation', p3.pelvisRotationMinDeg) &&
    meets('HipAbduction', p3.hipAbductionMinDeg)
  );

  const meetsP2 = (
    meets('ShoulderIR', p2.shoulderIRMinDeg) &&
    meets('ShoulderAbduction', p2.shoulderAbdMinDeg) &&
    meets('PelvicRotation', p2.pelvisRotationMinDeg) &&
    meets('HipAbduction', p2.hipAbductionMinDeg) &&
    meets('HipExtension', p2.hipExtensionMinDeg)
  );

  if (meetsP3) return 'P3';
  if (meetsP2) return 'P2';
  return 'P1';
}

/**
 * Backwards compatibility wrapper for computeSopPhase
 */
export const computePhaseCode = computePhaseCodeLegacy;
export const computePhaseFromSop = computePhaseCodeLegacy;

function computePhaseCodeLegacy(phaseGoals?: Partial<SopPhaseGoals>, formValues?: FormValues): 'P1' | 'P2' | 'P3' {
  if (!formValues) return 'P1';
  return computeSopPhase(formValues, phaseGoals);
}

/**
 * Computes SOP Analysis buckets (left / right / intact).
 */
export function computeSopAnalysis(
  formValues: FormValues,
  testData: TestData,
  phaseGoals: SopPhaseGoals | Record<string, any>,
  analysisThreshold: number,
): Analysis | null {
  const hasValues = Object.values(formValues).some(v => v !== null && v !== '' && v !== 'Select...');
  if (!hasValues) return null;

  const newAnalysis: Analysis = { left: [], right: [], intact: [] };

  const getNominal = (key: string) => {
    switch (key) {
      case 'CervicalAxialRotation': return phaseGoals.cervicalRotationMinDeg ?? null;
      case 'CervicalLateralFlexion': return phaseGoals.cervicalLatFlexMinDeg ?? null;
      case 'ShoulderIR': return phaseGoals.shoulderIRMinDeg ?? null;
      case 'ShoulderAbduction': return phaseGoals.shoulderAbdMinDeg ?? null;
      case 'PelvicRotation': return phaseGoals.pelvisRotationMinDeg ?? null;
      case 'HipAbduction': return phaseGoals.hipAbductionMinDeg ?? null;
      case 'StraightLegRaise': return phaseGoals.slrMinDeg ?? null;
      case 'HipExtension': return phaseGoals.hipExtensionMinDeg ?? null;
      default: return null;
    }
  };

  for (const key in testData) {
    const test = testData[key];
    if (test.type === 'text' || key === 'FootPresentation') continue;

    const leftVal = parseValue(`L_${key}`, formValues, testData);
    const rightVal = parseValue(`R_${key}`, formValues, testData);
    const overrideNominal = getNominal(key);
    const nominalVal = overrideNominal !== null ? overrideNominal : parseFloat(test.nominal as string);
    const threshold = (Math.abs(nominalVal) * analysisThreshold) / 100;

    let isLeftPassing = false, isRightPassing = false, isLeftHyper = false, isRightHyper = false;

    if (leftVal !== null) {
      isLeftPassing = leftVal >= nominalVal;
      isLeftHyper = leftVal > nominalVal;
    }
    if (rightVal !== null) {
      isRightPassing = rightVal >= nominalVal;
      isRightHyper = rightVal > nominalVal;
    }

    if (leftVal !== null || rightVal !== null) {
      let isFunctionalOccupancy = false;
      const isLeftBetter = leftVal !== null && rightVal !== null && leftVal > rightVal;
      const isRightBetter = leftVal !== null && rightVal !== null && rightVal > leftVal;
      const isEven = leftVal !== null && rightVal !== null && Math.abs(leftVal - rightVal) <= threshold;

      if ((isLeftPassing && isLeftBetter) || (isRightPassing && isRightBetter) || (isLeftPassing && isRightPassing && isEven)) {
        isFunctionalOccupancy = true;
      }

      const result: AnalysisResultItem = {
        test: test.label,
        leftVal,
        rightVal,
        nominal: nominalVal,
        isLeftPassing,
        isRightPassing,
        isLeftHyper,
        isRightHyper,
        isFunctionalOccupancy,
        leftIntakeGoalFrame: getSopIntakeGoalFrameState(leftVal, nominalVal),
        rightIntakeGoalFrame: getSopIntakeGoalFrameState(rightVal, nominalVal),
      };

      if (leftVal !== null && rightVal !== null) {
        if (leftVal < rightVal - threshold) { newAnalysis.left.push(result); }
        else if (rightVal < leftVal - threshold) { newAnalysis.right.push(result); }
        else { newAnalysis.intact.push(result); }
      } else if (leftVal !== null) {
        if (isLeftPassing) newAnalysis.intact.push(result); else newAnalysis.right.push(result);
      } else if (rightVal !== null) {
        if (isRightPassing) newAnalysis.intact.push(result); else newAnalysis.left.push(result);
      }
    }
  }

  return newAnalysis;
}

/**
 * Unified posture computation from SOP.
 */
export function computeSopPosture(formValues: FormValues, analysis: Analysis | null, dynamicGoals?: Partial<SopPhaseGoals>): SopPostureResult {
  const tokens = computeCollapsedTokensFromSop(formValues, analysis, dynamicGoals);
  const limitations = computeMovementLimitations(formValues);

  return {
    pattern: tokens.pattern ?? null,
    lateralized: tokens.lateralized ?? null,
    grounded: tokens.grounded ?? null,
    phase: tokens.phase ?? 'P1',
    movementLimitations: limitations,
    infrasternalAngle: tokens.infrasternalAngle ?? null,
    isaPattern: tokens.isaPattern ?? null,
  };
}

export function computeCollapsedTokensFromSop(formValues: FormValues, analysis: Analysis | null, phaseGoals?: Partial<SopPhaseGoals>): CollapsedTokens {
  const tokens: CollapsedTokens = { phase: 'P1' };
  
  const lPR = parseNum(formValues['L_PelvicRotation']);
  const rPR = parseNum(formValues['R_PelvicRotation']);
  if (lPR !== null && rPR !== null) {
    tokens.lateralized = rPR > lPR ? 'Right' : (lPR > rPR ? 'Left' : undefined);
  }

  const lG = parseNum(formValues['L_GroundingPerformance']);
  const rG = parseNum(formValues['R_GroundingPerformance']);
  if (lG !== null && rG !== null) tokens.grounded = rG > lG ? 'Right' : (lG > rG ? 'Left' : undefined);

  const fp = String(formValues['FootPresentation'] || '').toLowerCase();
  if (fp) {
    if (fp.includes('l ] supinated') || fp.includes('r ] > [ l ]') || fp.includes('r ] >') || fp.includes('l supinated')) {
      tokens.leftFoot = 'Supinated';
      tokens.rightFoot = 'Pronated';
    } else if (fp.includes('r ] supinated') || fp.includes('l ] > [ r ]') || fp.includes('l ] >') || fp.includes('r supinated')) {
      tokens.leftFoot = 'Pronated';
      tokens.rightFoot = 'Supinated';
    }
  }

  const L = (k: string) => parseNum(formValues[`L_${k}`]);
  const R = (k: string) => parseNum(formValues[`R_${k}`]);
  const avg = (l: number | null, r: number | null) => (l !== null && r !== null) ? (l + r) / 2 : null;

  const avgSLR = avg(L('StraightLegRaise'), R('StraightLegRaise'));
  const avgABD = avg(L('ShoulderAbduction'), R('ShoulderAbduction'));
  const avgHA = avg(L('HipAbduction'), R('HipAbduction'));
  const avgIR = avg(L('ShoulderIR'), R('ShoulderIR'));

  const lFir = L('FemoralIR');
  const rFir = R('FemoralIR');
  const minFemoralIr = lFir !== null && rFir !== null ? Math.min(lFir, rFir) : null;

  const sumFERminusFIR = (() => {
    const fir = (L('FemoralIR') || 0) + (R('FemoralIR') || 0);
    const fer = (L('FemoralER') || 0) + (R('FemoralER') || 0);
    return fer - fir;
  })();

  const patterns = evaluationRules().sop.patterns;
  const sbRules = patterns.sb as {
    avgSlrMax: number;
    avgAbdMax: number;
    slrHighDegMin?: number;
    fakeSlrMinFemoralIrDeg?: number;
    obersPerSideFailMaxDeg?: number;
  };

  const slrHighMin = sbRules.slrHighDegMin;
  const fakeIrMax = sbRules.fakeSlrMinFemoralIrDeg;
  const fakeSlrSwaybackFlag =
    slrHighMin !== undefined &&
    slrHighMin !== null &&
    fakeIrMax !== undefined &&
    avgSLR !== null &&
    minFemoralIr !== null &&
    avgSLR >= slrHighMin &&
    minFemoralIr <= fakeIrMax;

  const lOber = L('HipAdductionObers');
  const rOber = R('HipAdductionObers');
  const oberFailMax = sbRules.obersPerSideFailMaxDeg;
  const obersBothRestricted =
    oberFailMax !== undefined &&
    oberFailMax !== null &&
    lOber !== null &&
    rOber !== null &&
    lOber <= oberFailMax &&
    rOber <= oberFailMax;

  if (
    fakeSlrSwaybackFlag ||
    obersBothRestricted ||
    (avgSLR !== null && avgSLR < sbRules.avgSlrMax && avgABD !== null && avgABD < sbRules.avgAbdMax) ||
    sumFERminusFIR > 0
  ) {
    tokens.pattern = 'SB';
  } else if (
    (avgIR !== null && avgIR < patterns.apt.shoulderIrMax && avgHA !== null && avgHA < patterns.apt.avgHipAbdMax) ||
    sumFERminusFIR < 0
  ) {
    tokens.pattern = 'APT';
  }

  const isaVal = parseNum(formValues['InfrasternalAngle']);
  if (isaVal !== null) {
    tokens.infrasternalAngle = isaVal;
    tokens.isaPattern = isaVal <= 90 ? 'Narrow' : 'Wide';
  }

  tokens.phase = computeSopPhase(formValues, phaseGoals);
  return tokens;
}

export function computeOverallDisplay(
  formValues: FormValues,
  analysis: Analysis | null,
  clientPatternCode?: string,
  phaseCode: 'P1' | 'P2' | 'P3' = 'P1'
): OverallDisplay {
  let lateralizationLabel: 'RLat' | 'LLat' | 'Mixed' | 'Balanced' = 'Mixed';
  if (analysis) {
    const left = analysis.left?.length || 0;
    const right = analysis.right?.length || 0;
    const intact = analysis.intact?.length || 0;
    if (intact >= left + right && intact > 0) lateralizationLabel = 'Balanced';
    else if (right > left) lateralizationLabel = 'RLat';
    else if (left > right) lateralizationLabel = 'LLat';
    else lateralizationLabel = 'Mixed';
  }

  let patternLabel: 'SB' | 'AO' | undefined;
  if (clientPatternCode) {
    const first = clientPatternCode.split(',')[0]?.trim().toUpperCase();
    if (first.startsWith('SB')) patternLabel = 'SB';
    else if (first.startsWith('APT') || first.startsWith('AO')) patternLabel = 'AO';
  }

  const phases = evaluationRules().sop.display.phases;
  const phaseLabel = phaseCode === 'P3' ? phases.P3 : phaseCode === 'P2' ? phases.P2 : phases.P1;

  const parts: string[] = [];
  if (patternLabel) parts.push(patternLabel);
  parts.push(lateralizationLabel);
  parts.push(phaseLabel);

  return {
    lateralizationLabel,
    phaseLabel,
    patternLabel,
    fullText: parts.join(' '),
    hasFallback: false,
  };
}

export function computeSectionSummaries(formValues: FormValues): SectionSummaries {
  const L = (key: string) => parseNum(formValues[`L_${key}`]);
  const R = (key: string) => parseNum(formValues[`R_${key}`]);

  // Cervical
  const carL = L('CervicalAxialRotation');
  const carR = R('CervicalAxialRotation');
  const clfL = L('CervicalLateralFlexion');
  const clfR = R('CervicalLateralFlexion');
  const carDisp = carL !== null && carR !== null ? `${carL} ${carL < carR ? '<' : carL > carR ? '>' : '='} ${carR}` : 'N/A';
  const clfDisp = clfL !== null && clfR !== null ? `${clfL} ${clfL < clfR ? '<' : clfL > clfR ? '>' : '='} ${clfR}` : 'N/A';
  let cervSummary: 'Left' | 'Right' | 'Mixed' | 'N/A' = 'N/A';
  if (carL !== null && carR !== null && clfL !== null && clfR !== null) {
    const leftWins = (carL > carR ? 1 : 0) + (clfL > clfR ? 1 : 0);
    const rightWins = (carR > carL ? 1 : 0) + (clfR > clfL ? 1 : 0);
    cervSummary = leftWins > rightWins ? 'Left' : rightWins > leftWins ? 'Right' : 'Mixed';
  }

  // Arms/Torso
  const lIR = L('ShoulderIR');
  const rIR = R('ShoulderIR');
  const lER = L('ShoulderER');
  const rER = R('ShoulderER');
  const lABD = L('ShoulderAbduction');
  const rABD = R('ShoulderAbduction');

  const biases = evaluationRules().sop.display.biases;
  const leftArm = lIR !== null && lER !== null ? (lIR > lER ? biases.ir : (lER > lIR ? biases.er : biases.mixed)) : 'N/A';
  const rightArm = rIR !== null && rER !== null ? (rIR > rER ? biases.ir : (rER > rIR ? biases.er : biases.mixed)) : 'N/A';
  const torso = (rER !== null && lIR !== null && rABD !== null && lABD !== null)
    ? ((rER + rABD) > (lIR + lABD) ? biases.posteriorExpansion : (lIR + lABD) > (rER + rABD) ? biases.anteriorExpansion : biases.mixed)
    : 'N/A';

  // Pelvis
  const lPR = L('PelvicRotation');
  const rPR = R('PelvicRotation');
  const lSLR = L('StraightLegRaise');
  const rSLR = R('StraightLegRaise');
  const lO = parseNum(formValues['L_HipExtension']);
  const rO = parseNum(formValues['R_HipExtension']);
  const lHA = L('HipAbduction');
  const rHA = R('HipAbduction');

  let pelvisScore = 0; 
  if (lPR !== null && rPR !== null) pelvisScore += rPR > lPR ? 1 : lPR > rPR ? -1 : 0;
  if (lSLR !== null && rSLR !== null) pelvisScore += rSLR > lSLR ? 1 : lSLR > rSLR ? -1 : 0;
  if (lO !== null && rO !== null) pelvisScore += (rO >= 0 ? 1 : 0) - (lO >= 0 ? 1 : 0);
  if (lHA !== null && rHA !== null) pelvisScore += lHA > rHA ? -1 : rHA > lHA ? 1 : 0; 
  const pelvisSummary = pelvisScore > 0 ? 'Right' : pelvisScore < 0 ? 'Left' : 'Mixed';
  const pelvisDetails = `PR ${lPR ?? 'N/A'} vs ${rPR ?? 'N/A'}, SLR ${lSLR ?? 'N/A'} vs ${rSLR ?? 'N/A'}, HipExt ${lO ?? 'N/A'} vs ${rO ?? 'N/A'}, HA ${lHA ?? 'N/A'} vs ${rHA ?? 'N/A'}`;

  // Legs 
  const lFIR = L('FemoralIR');
  const rFIR = R('FemoralIR');
  const lFER = L('FemoralER');
  const rFER = R('FemoralER');
  const firBias = (lFIR !== null && rFIR !== null) ? (lFIR > rFIR ? 'Left' : rFIR > lFIR ? 'Right' : 'Mixed') : 'N/A';
  const ferBias = (lFER !== null && rFER !== null) ? (lFER > rFER ? 'Left' : rFER > lFER ? 'Right' : 'Mixed') : 'N/A';

  // Foot & Grounding
  const footPresentation = String(formValues['FootPresentation'] || 'N/A');
  const lG = String(formValues['L_GroundingPerformance'] || 'N/A');
  const rG = String(formValues['R_GroundingPerformance'] || 'N/A');
  let grounding = 'N/A';
  if (lG !== 'N/A' || rG !== 'N/A') {
    if (lG !== 'N/A' && rG !== 'N/A') grounding = `${rG === lG ? 'Even' : (rG.length > lG.length ? 'R > L' : 'L > R')} (${lG} | ${rG})`;
    else grounding = lG !== 'N/A' ? `L: ${lG}` : `R: ${rG}`;
  }

  // Construct standard SectionSummaries format (with hasData / missing flags to appease the UI components that expect it)
  // Our legacy function just returned simple objects but UI components often expect missing text
  const has = (v: any) => v !== 'N/A';

  return {
    cervical: { 
      car: carDisp, clf: clfDisp, summary: cervSummary,
      hasData: has(cervSummary), missing: [], missingText: '', content: '' 
    },
    arms: { 
      leftArm, torso, rightArm,
      hasData: has(torso), missing: [], missingText: '', content: '' 
    },
    pelvis: { 
      summary: pelvisSummary, details: pelvisDetails,
      hasData: has(pelvisSummary), missing: [], missingText: '', content: '' 
    },
    legs: { 
      firBias, ferBias,
      hasData: has(firBias), missing: [], missingText: '', content: '' 
    },
    foot: { 
      footPresentation, grounding,
      hasData: has(grounding), missing: [], missingText: '', content: '' 
    }
  } as any; // Using exact legacy structure
}

